import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

import RequireAuth from "@/components/features/auth/RequireAuth";
import { PageContentSkeleton } from "@/components/features/common/PageContentSkeleton";
import AuthLayout from "@/layouts/AuthLayout";
import Layout from "@/layouts/Layout";
import RouteError from "@/views/route-error";

const pages = {
	authCallback: () => import("@/views/auth-callback"),
	fleetValuation: () => import("@/views/fleet-valuation"),
	login: () => import("@/views/login"),
	marketIntelligence: () => import("@/views/market-intelligence"),
	profitabilitySimulator: () => import("@/views/profitability-simulator"),
	reports: () => import("@/views/reports"),
	shipDatabase: () => import("@/views/ship-database"),
	signUp: () => import("@/views/sign-up"),
	shipPrice: () => import("@/views/vessel-market-values"),
	verifyEmail: () => import("@/views/verify-email"),
	onBoarding: () => import("@/views/onboarding"),
};

const AuthCallbackPage = lazy(pages.authCallback);
const FleetValuationPage = lazy(pages.fleetValuation);
const LoginPage = lazy(pages.login);
const MarketIntelligencePage = lazy(pages.marketIntelligence);
const ProfitabilitySimulatorPage = lazy(pages.profitabilitySimulator);
const ReportsPage = lazy(pages.reports);
const ShipDatabasePage = lazy(pages.shipDatabase);
const SignUpPage = lazy(pages.signUp);
const ShipPricePage = lazy(pages.shipPrice);
const VerifyEmailPage = lazy(pages.verifyEmail);
const OnboardingPage = lazy(pages.onBoarding);

const ROUTE_CHUNK_PRELOADERS: Record<string, () => Promise<unknown>> = {
	"/": pages.marketIntelligence,
	"/fleet-valuation": pages.fleetValuation,
	"/profitability-simulator": pages.profitabilitySimulator,
	"/reports": pages.reports,
	"/ship-database": pages.shipDatabase,
	"/vessel-market-values": pages.shipPrice,
	"/auth/login": pages.login,
	"/auth/signup": pages.signUp,
	"/auth/callback": pages.authCallback,
	"/auth/verify-email": pages.verifyEmail,
	"/onboarding": pages.onBoarding,
};

export function preloadRouteChunk(pathname: string) {
	const loader = ROUTE_CHUNK_PRELOADERS[pathname];
	if (loader) void loader();
}

function PageLoader() {
	return (
		<div className="flex h-full min-h-[60vh] w-full items-start justify-center p-6">
			<PageContentSkeleton className="mx-auto 3xl:max-w-340" />
		</div>
	);
}

function S({ children }: { children: React.ReactNode }) {
	return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
	{
		errorElement: <RouteError />,
		element: <RequireAuth />,
		children: [
			{
				path: "/onboarding",
				element: (
					<S>
						<OnboardingPage />
					</S>
				),
			},
			{
				path: "/",
				element: <Layout />,
				children: [
					{
						index: true,
						element: (
							<S>
								<MarketIntelligencePage />
							</S>
						),
					},
					{
						path: "fleet-valuation",
						element: (
							<S>
								<FleetValuationPage />
							</S>
						),
					},
					{
						path: "reports",
						element: (
							<S>
								<ReportsPage />
							</S>
						),
					},
					{
						path: "vessel-market-values",
						element: (
							<S>
								<ShipPricePage />
							</S>
						),
					},
					{
						path: "ship-database",
						element: (
							<S>
								<ShipDatabasePage />
							</S>
						),
					},
					{
						path: "profitability-simulator",
						element: (
							<S>
								<ProfitabilitySimulatorPage />
							</S>
						),
					},
					// V2 FEATURE
					// {
					// 	path: "market-analysis-reports",
					// 	element: <MarketAnalysisReports />,
					// },
					// {
					// 	path: "ai-intelligence",
					// 	element: <AIIntelligencePage />,
					// },
				],
			},
		],
	},
	{
		path: "/auth",
		element: <AuthLayout />,
		children: [
			{
				path: "login",
				element: (
					<S>
						<LoginPage />
					</S>
				),
			},
			{
				path: "signup",
				element: (
					<S>
						<SignUpPage />
					</S>
				),
			},
			{
				path: "callback",
				element: (
					<S>
						<AuthCallbackPage />
					</S>
				),
			},
			{
				path: "verify-email",
				element: (
					<S>
						<VerifyEmailPage />
					</S>
				),
			},
		],
	},
]);
